import { Box, Skeleton, Typography, Stack } from "@mui/material";
import { Gauge, gaugeClasses } from "@mui/x-charts/Gauge";
import { GridCellParams, GridColDef } from "@mui/x-data-grid-premium";
import { useTenantTranslation } from "hooks/formatters";
import { useLanguage, useTenant } from "hooks/settings";
import { useGetStrategyDashboardProductionPaceQuery } from "src/store/api/generatedApi";
import { formatTimestampWithOffset } from "src/utils/formatTimestampWithOffset";
import { HotkeyCompliantDataGrid } from "src/components/common/dataGrid";
import { useFormatNumber } from "src/utils/formatNumber";
import { OverviewCard } from "./OverviewCard";
import FactoryIcon from "@mui/icons-material/Factory";
import dayjs, { Dayjs } from "dayjs";
import { useMemo } from "react";
import { defaultTableSx } from "../strategyDashboard/tableFormatting";

interface ProductGroupRow {
  id: string;
  productGroup: string;
  planned: number;
  produced: number;
  remaining: number;
}

export const Production = ({ date }: { date: Dayjs }) => {
  const { t } = useTenantTranslation();
  const tenant = useTenant();
  const language = useLanguage();
  dayjs.locale(language);
  const monthName = date.format("MMMM");

  return (
    <OverviewCard
      title={t("monthProduction", { month: monthName })}
      icon={FactoryIcon}
      buttonText={t("viewProduction")}
      buttonHref={`/${tenant}/dashboard/production`}
    >
      <ProductionContent date={date} tenant={tenant} />
    </OverviewCard>
  );
};

const ProductionContent = ({
  date,
  tenant,
}: {
  date: Dayjs;
  tenant: string;
}) => {
  const { t } = useTenantTranslation();
  const formatNumber = useFormatNumber();

  const { data, isFetching } = useGetStrategyDashboardProductionPaceQuery({
    tenantName: tenant,
    on: formatTimestampWithOffset(date),
  });

  const { productGroups, totalPlanned, totalProduced, totalRemaining } =
    useMemo(() => {
      if (!data || data.series.length === 0) {
        return {
          productGroups: [],
          totalPlanned: 0,
          totalProduced: 0,
          totalRemaining: 0,
        };
      }

      const filteredSeries = data.series.filter(
        (item) =>
          item.production_source === "level2" &&
          item.production_type === "billets"
      );

      const productGroupMap = new Map<
        string,
        { produced: number; planned: number }
      >();

      for (const item of filteredSeries) {
        productGroupMap.set(item.product_group_name, {
          produced: item.produced_mass_mtd,
          planned: item.expected_monthly_production,
        });
      }

      const groups: ProductGroupRow[] = Array.from(
        productGroupMap.entries()
      ).map(([productGroup, values]) => {
        const planned = Math.round(values.planned);
        const produced = Math.round(values.produced);
        return {
          id: productGroup,
          productGroup,
          planned,
          produced,
          remaining: planned - produced,
        };
      });

      const planned = groups.reduce((acc, pg) => acc + pg.planned, 0);
      const produced = groups.reduce((acc, pg) => acc + pg.produced, 0);

      return {
        productGroups: groups,
        totalPlanned: planned,
        totalProduced: produced,
        totalRemaining: planned - produced,
      };
    }, [data]);

  const columns: GridColDef<ProductGroupRow>[] = useMemo(
    () => [
      {
        field: "productGroup",
        headerName: t("product"),
        flex: 1,
        minWidth: 100,
      },
      {
        field: "planned",
        headerName: t("planned"),
        flex: 0.7,
        minWidth: 80,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value: number) => formatNumber(0)(value),
      },
      {
        field: "produced",
        headerName: t("produced"),
        flex: 0.7,
        minWidth: 80,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value: number) => formatNumber(0)(value),
      },
      {
        field: "remaining",
        headerName: t("remaining"),
        flex: 0.7,
        minWidth: 80,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value: number) => formatNumber(0)(value),
        renderCell: ({ row }) =>
          row.remaining < 0 ? (
            <Box
              component="span"
              sx={{ color: "text.secondary", fontStyle: "italic" }}
            >
              {formatNumber(0)(row.remaining)}
            </Box>
          ) : (
            formatNumber(0)(row.remaining)
          ),
      },
    ],
    [t, formatNumber]
  );

  const totalRow: ProductGroupRow = useMemo(
    () => ({
      id: "TOTAL",
      productGroup: t("total"),
      planned: totalPlanned,
      produced: totalProduced,
      remaining: totalRemaining,
    }),
    [t, totalPlanned, totalProduced, totalRemaining]
  );

  if (isFetching) return <Skeleton variant="rectangular" height={300} />;
  if (!data || data.series.length === 0)
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <Typography variant="body1" sx={{ color: "text.secondary" }}>
          {t("noData")}
        </Typography>
      </Box>
    );

  return (
    <Stack direction="row" sx={{ height: "100%", px: 2, pb: 1, gap: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minWidth: 180,
          flex: 0.7,
        }}
      >
        <Box sx={{ position: "relative" }}>
          <Gauge
            width={200}
            height={130}
            value={totalProduced}
            valueMin={0}
            valueMax={totalPlanned || 1}
            startAngle={-100}
            endAngle={100}
            sx={{
              [`& .${gaugeClasses.valueText}`]: {
                display: "none",
              },
              [`& .${gaugeClasses.valueArc}`]: {
                fill: "primary.main",
              },
              [`& .${gaugeClasses.referenceArc}`]: {
                opacity: 0.2,
              },
            }}
          />
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -10%)",
              textAlign: "center",
            }}
          >
            <Typography variant="h5" fontWeight="fontWeightSemiBold">
              {formatNumber(0)(totalProduced)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t("tons")}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              width: "90%",
              mx: 2,
              mt: -1,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {`0`}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatNumber(0)(totalPlanned)}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          alignItems: "center",
          height: "100%",
        }}
      >
        <HotkeyCompliantDataGrid
          rows={productGroups}
          columns={columns}
          pinnedRows={{ bottom: [totalRow] }}
          getCellClassName={(params: GridCellParams<ProductGroupRow>) =>
            params.row.id === "TOTAL" ? "total-row" : ""
          }
          sx={{
            ...defaultTableSx,
            mt: 0,
            height: "90%",
            "& .total-row": {
              fontWeight: "fontWeightBold",
            },
          }}
        />
      </Box>
    </Stack>
  );
};
